import { lazy, Suspense } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { AppShell } from "@/layouts/AppShell";
import { RequireAuth } from "@/router/RequireAuth";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Skeleton } from "@/components/ui/Skeleton";
import LoginPage from "@/pages/LoginPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";

const PrivacyPolicyPage = lazy(() => import("@/pages/PrivacyPolicyPage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const DashboardPage = lazy(() => import("@/modules/dashboard/DashboardPage"));
const BiLayout = lazy(() => import("@/modules/business-intelligence/BiLayout"));
const BiOverviewPage = lazy(() => import("@/modules/business-intelligence/BiOverviewPage"));
const BiTrafficPage = lazy(() => import("@/modules/business-intelligence/BiTrafficPage"));
const BiSeoIntelPage = lazy(() => import("@/modules/business-intelligence/BiSeoIntelPage"));
const BiKeywordsIntelPage = lazy(() => import("@/modules/business-intelligence/BiKeywordsIntelPage"));
const BiOpportunitiesPage = lazy(() => import("@/modules/business-intelligence/BiOpportunitiesPage"));
const BiInsightsPage = lazy(() => import("@/modules/business-intelligence/BiInsightsPage"));
const BiChatPage = lazy(() => import("@/modules/business-intelligence/BiChatPage"));
const BiConnectorsPage = lazy(() => import("@/modules/business-intelligence/BiConnectorsPage"));
const CompetitorIntelligenceLayout = lazy(
  () => import("@/modules/competitor-intelligence/CompetitorIntelligenceLayout"),
);
const CiDashboardPage = lazy(() => import("@/modules/competitor-intelligence/CiDashboardPage"));
const CiAddCompetitorPage = lazy(() => import("@/modules/competitor-intelligence/CiAddCompetitorPage"));
const CiScanProgressPage = lazy(() => import("@/modules/competitor-intelligence/CiScanProgressPage"));
const CiCompetitorsPage = lazy(() => import("@/modules/competitor-intelligence/CiCompetitorsPage"));
const CiCompetitorDetailPage = lazy(() => import("@/modules/competitor-intelligence/CiCompetitorDetailPage"));
const CiChangeDetailPage = lazy(() => import("@/modules/competitor-intelligence/CiChangeDetailPage"));
const CiAlertsPage = lazy(() => import("@/modules/competitor-intelligence/CiAlertsPage"));
const CiOpportunitiesPage = lazy(() => import("@/modules/competitor-intelligence/CiOpportunitiesPage"));
const CiComparePage = lazy(() => import("@/modules/competitor-intelligence/CiComparePage"));
const CiReportsPage = lazy(() => import("@/modules/competitor-intelligence/CiReportsPage"));
const CiIntelligencePage = lazy(() => import("@/modules/competitor-intelligence/CiIntelligencePage"));
const SeoPage = lazy(() => import("@/modules/seo/SeoPage"));
const KeywordResearchPage = lazy(() => import("@/modules/seo/KeywordResearchPage"));
const NewsIntelligencePage = lazy(() => import("@/modules/news-intelligence/NewsIntelligencePage"));
const NewsArticleDetailsPage = lazy(() => import("@/modules/news-intelligence/NewsArticleDetailsPage"));
const ContentStudioPage = lazy(() => import("@/modules/content-studio/ContentStudioPage"));
const SocialMediaPage = lazy(() => import("@/modules/social-media/SocialMediaPage"));
const ImageStudioPage = lazy(() => import("@/modules/image-studio/ImageStudioPage"));
const VideoStudioPage = lazy(() => import("@/modules/video-studio/VideoStudioPage"));
const YouTubeLayout = lazy(() => import("@/modules/youtube/YouTubeLayout"));
const YouTubeAnalyticsPage = lazy(() => import("@/modules/youtube/YouTubeAnalyticsPage"));
const YouTubeShortsPage = lazy(() => import("@/modules/youtube/YouTubeShortsPage"));
const YouTubeQuotaPage = lazy(() => import("@/modules/youtube/YouTubeQuotaPage"));
const YouTubeVideosPage = lazy(() => import("@/modules/youtube/YouTubeVideosPage"));
const YouTubeCommentsPage = lazy(() => import("@/modules/youtube/YouTubeCommentsPage"));
const YouTubePublishPage = lazy(() => import("@/modules/youtube/YouTubePublishPage"));
const YouTubeAiPage = lazy(() => import("@/modules/youtube/YouTubeAiPage"));
const YouTubeShortsManagerPage = lazy(() => import("@/modules/youtube/YouTubeShortsManagerPage"));
const YouTubeLivePage = lazy(() => import("@/modules/youtube/YouTubeLivePage"));
const YouTubePlaylistsPage = lazy(() => import("@/modules/youtube/YouTubePlaylistsPage"));
const YouTubeCaptionsPage = lazy(() => import("@/modules/youtube/YouTubeCaptionsPage"));
const YouTubeModerationPage = lazy(() => import("@/modules/youtube/YouTubeModerationPage"));
const BacklinksPage = lazy(() => import("@/modules/backlinks/BacklinksPage"));
const AnalyticsPage = lazy(() => import("@/modules/analytics/AnalyticsPage"));
const WorkflowPage = lazy(() => import("@/modules/workflow/WorkflowPage"));
const IntegrationsPage = lazy(() => import("@/modules/integrations/IntegrationsPage"));
const SettingsLayout = lazy(() => import("@/modules/settings/SettingsLayout"));
const SettingsPage = lazy(() => import("@/modules/settings/SettingsPage"));
const TeamPage = lazy(() => import("@/modules/settings/TeamPage"));
const BusinessKnowledgePage = lazy(() => import("@/modules/settings/BusinessKnowledgePage"));
const ProfilePage = lazy(() => import("@/modules/profile/ProfilePage"));
const HelpPage = lazy(() => import("@/modules/help/HelpPage"));
const SearchPage = lazy(() => import("@/modules/search/SearchPage"));
const NotificationsPage = lazy(() => import("@/modules/notifications/NotificationsPage"));

function LazyFallback() {
  return (
    <div className="space-y-3">
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-40 w-full" />
    </div>
  );
}

export function AppRouter() {
  const basename = (import.meta.env.BASE_URL || "/").replace(/\/$/, "") || "/";

  return (
    <BrowserRouter basename={basename === "/" ? undefined : basename}>
      <ErrorBoundary>
        <Suspense fallback={<LazyFallback />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/data-deletion" element={<PrivacyPolicyPage />} />
            <Route
              element={
                <RequireAuth>
                  <AppShell />
                </RequireAuth>
              }
            >
              <Route index element={<DashboardPage />} />
              <Route path="business-intelligence" element={<BiLayout />}>
                <Route index element={<BiOverviewPage />} />
                <Route path="traffic" element={<BiTrafficPage />} />
                <Route path="seo" element={<BiSeoIntelPage />} />
                <Route path="keywords" element={<BiKeywordsIntelPage />} />
                <Route path="opportunities" element={<BiOpportunitiesPage />} />
                <Route path="insights" element={<BiInsightsPage />} />
                <Route path="chat" element={<BiChatPage />} />
                <Route path="connectors" element={<BiConnectorsPage />} />
              </Route>
              <Route path="competitor-intelligence" element={<CompetitorIntelligenceLayout />}>
                <Route index element={<CiDashboardPage />} />
                <Route path="add" element={<CiAddCompetitorPage />} />
                <Route path="scan/:scanId" element={<CiScanProgressPage />} />
                <Route path="competitors" element={<CiCompetitorsPage />} />
                <Route path="competitors/:competitorId" element={<CiCompetitorDetailPage />} />
                <Route path="competitors/:competitorId/changes/:changeId" element={<CiChangeDetailPage />} />
                <Route path="intelligence" element={<CiIntelligencePage />} />
                <Route path="alerts" element={<CiAlertsPage />} />
                <Route path="opportunities" element={<CiOpportunitiesPage />} />
                <Route path="compare" element={<CiComparePage />} />
                <Route path="reports" element={<CiReportsPage />} />
              </Route>
              <Route path="seo" element={<SeoPage />} />
              <Route path="keyword-research" element={<KeywordResearchPage />} />
              <Route path="news-intelligence" element={<NewsIntelligencePage />} />
              <Route path="news-intelligence/articles/:articleId" element={<NewsArticleDetailsPage />} />
              <Route path="news-intelligence/article/:articleId" element={<NewsArticleDetailsPage />} />
              <Route path="business-knowledge" element={<Navigate to="/settings/business-knowledge" replace />} />
              <Route path="documents" element={<Navigate to="/settings/business-knowledge" replace />} />
              <Route path="content-studio" element={<ContentStudioPage />} />
              <Route path="social-media" element={<SocialMediaPage />} />
              <Route path="youtube" element={<YouTubeLayout />}>
                <Route index element={<YouTubeAnalyticsPage />} />
                <Route path="shorts" element={<YouTubeShortsPage />} />
                <Route path="quota" element={<YouTubeQuotaPage />} />
                <Route path="shorts-library" element={<YouTubeShortsManagerPage />} />
                <Route path="videos" element={<YouTubeVideosPage />} />
                <Route path="playlists" element={<YouTubePlaylistsPage />} />
                <Route path="live" element={<YouTubeLivePage />} />
                <Route path="comments" element={<YouTubeCommentsPage />} />
                <Route path="moderation" element={<YouTubeModerationPage />} />
                <Route path="captions" element={<YouTubeCaptionsPage />} />
                <Route path="publish" element={<YouTubePublishPage />} />
                <Route path="ai" element={<YouTubeAiPage />} />
              </Route>
              <Route path="image-studio" element={<ImageStudioPage />} />
              <Route path="video-studio" element={<VideoStudioPage />} />
              <Route path="backlinks" element={<BacklinksPage />} />
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="workflow" element={<WorkflowPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route path="settings" element={<SettingsLayout />}>
                <Route index element={<SettingsPage />} />
                <Route path="team" element={<TeamPage />} />
                <Route path="business-knowledge" element={<BusinessKnowledgePage />} />
              </Route>
              <Route path="profile" element={<ProfilePage />} />
              <Route path="help" element={<HelpPage />} />
              <Route path="search" element={<SearchPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
}
